import { useState, useEffect, useRef } from 'react';
import { Upload, FileText, X, CheckCircle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import AnalysisResults from '@/components/analysis-results';
import LeadCaptureDialog, { LeadData } from '@/components/lead-capture-dialog';
import Hls from 'hls.js';
import { AnalysisResult } from '@/lib/analysis-schema';
import creditReportMoneyLogo from '@/assets/credit-report-money-logo.png';

interface BureauFiles {
  experian: File | null;
  equifax: File | null;
  transunion: File | null;
}

export default function Analyze() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [files, setFiles] = useState<BureauFiles>({
    experian: null,
    equifax: null,
    transunion: null
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [leadData, setLeadData] = useState<LeadData | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const videoSrc = 'https://customer-cbeadsgr09pnsezs.cloudflarestream.com/b17f76a1270818e8cdc55e8719b9ace8/manifest/video.m3u8';

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });
      hls.loadSource(videoSrc);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });

      return () => {
        hls.destroy();
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = videoSrc;
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(() => {});
      });
    }
  }, []);

  const handleFileChange = (bureau: keyof BureauFiles) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile?.type === 'application/pdf') {
      setFiles(prev => ({ ...prev, [bureau]: selectedFile }));
      setError(null);
    } else {
      setError('Please upload a valid PDF file');
    }
  };

  const removeFile = (bureau: keyof BureauFiles) => {
    setFiles(prev => ({ ...prev, [bureau]: null }));
  };

  const handleAnalyzeClick = () => {
    const uploadedFiles = Object.values(files).filter(f => f !== null);
    if (uploadedFiles.length === 0) {
      setError('Please upload at least one credit report');
      return;
    }
    
    if (leadData) {
      handleAnalyze(leadData);
    } else {
      setShowLeadDialog(true);
    }
  };

  const handleLeadSubmit = (data: LeadData) => {
    setLeadData(data);
    setShowLeadDialog(false);
    handleAnalyze(data);
  };

  const handleAnalyze = async (lead: LeadData) => {
    setAnalyzing(true);
    setError(null);
    setProgress(0);

    try {
      const formData = new FormData();
      if (files.experian) formData.append('experian', files.experian);
      if (files.equifax) formData.append('equifax', files.equifax);
      if (files.transunion) formData.append('transunion', files.transunion);
      
      formData.append('leadName', lead.name);
      formData.append('leadEmail', lead.email);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-report`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Analysis request failed');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let partialRead = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          partialRead += decoder.decode(value, { stream: true });
          const lines = partialRead.split('\n');
          partialRead = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                setAnalyzing(false);
                return;
              }
              try {
                const parsed = JSON.parse(data);
                if (parsed?.status === 'processing') {
                  if (typeof parsed?.progress === 'number') {
                    setProgress(prev => Math.max(prev, Math.min(parsed.progress, 99)));
                  }
                } else if (parsed?.status === 'completed') {
                  setResults(parsed?.result as AnalysisResult);
                  setProgress(100);
                  setAnalyzing(false);
                  return;
                } else if (parsed?.status === 'error') {
                  throw new Error(parsed?.message || 'Analysis failed');
                }
              } catch (e) {}
            }
          }
        }
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to analyze credit report. Check backend connection.');
      setAnalyzing(false);
    }
  };

  if (results) {
    return <AnalysisResults results={results} onReset={() => {
      setResults(null);
      setFiles({ experian: null, equifax: null, transunion: null });
    }} />;
  }

  const hasAnyFile = files.experian || files.equifax || files.transunion;

  const bureaus = [
    { key: 'experian' as keyof BureauFiles, name: 'Experian', color: 'primary' },
    { key: 'equifax' as keyof BureauFiles, name: 'Equifax', color: 'accent' },
    { key: 'transunion' as keyof BureauFiles, name: 'TransUnion', color: 'success' },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Video Background */}
      <video
        ref={videoRef}
        className="fixed inset-0 w-full h-full object-cover -z-20 opacity-20"
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="fixed inset-0 -z-10 bg-background/85" />
      
      {/* Ambient Orbs */}
      <div className="fixed inset-0 pointer-events-none -z-5">
        <div className="ambient-orb ambient-orb-primary absolute -top-[10%] -left-[10%] w-[40%] h-[40%]" />
        <div className="ambient-orb ambient-orb-secondary absolute bottom-[10%] -right-[5%] w-[30%] h-[30%]" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={creditReportMoneyLogo}
              alt="Credit Report Money"
              className="h-10 w-auto"
            />
          </Link>
          <Link to="/">
            <button className="px-5 py-2 text-sm rounded-full glass-panel border border-border/50 hover:border-primary/50 transition-all duration-300 font-medium">
              Back to Home
            </button>
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 pt-32 pb-16">
        {/* Hero */}
        <div className="text-center mb-12 animate-reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel border border-accent/20 text-accent mb-6">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">AI-Powered Analysis</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">
            <span className="gradient-text-gold">Analyze Your Credit Reports</span>
          </h1>
          <p className="text-lg text-muted-foreground mb-2">
            Upload reports from all three bureaus for comprehensive cross-bureau analysis
          </p>
          <p className="text-sm font-medium text-primary">
            Upload all three for the most accurate violation detection
          </p>
        </div>

        {/* Upload Card */}
        <div className="glass-panel-strong rounded-3xl p-8 md:p-12 border border-border/50 animate-reveal" style={{ animationDelay: '100ms' }}>
          <div className="space-y-8">
            {/* Upload Areas for Each Bureau */}
            <div className="grid md:grid-cols-3 gap-6">
              {bureaus.map((bureau, idx) => (
                <div key={bureau.key} className="space-y-3" style={{ animationDelay: `${idx * 100}ms` }}>
                  <h3 className={`text-lg font-semibold text-center text-${bureau.color}`}>{bureau.name}</h3>
                  <div
                    className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-300 ${
                      files[bureau.key]
                        ? `border-${bureau.color} bg-${bureau.color}/10`
                        : 'border-border/50 bg-card/30 hover:border-primary/50 hover:bg-primary/5'
                    }`}
                  >
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange(bureau.key)}
                      className="hidden"
                      id={`${bureau.key}-upload`}
                      disabled={analyzing}
                    />
                    <label htmlFor={`${bureau.key}-upload`} className="cursor-pointer block">
                      {files[bureau.key] ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className={`w-14 h-14 rounded-xl bg-${bureau.color}/10 border border-${bureau.color}/20 flex items-center justify-center`}>
                            <FileText className={`w-7 h-7 text-${bureau.color}`} />
                          </div>
                          <p className="text-sm font-semibold truncate max-w-full text-foreground">
                            {files[bureau.key]?.name}
                          </p>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              removeFile(bureau.key);
                            }}
                            className={`text-xs text-${bureau.color} hover:opacity-80 flex items-center gap-1 transition-opacity`}
                          >
                            <X className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-14 h-14 rounded-xl bg-muted/50 border border-border/50 flex items-center justify-center">
                            <Upload className="w-7 h-7 text-muted-foreground" />
                          </div>
                          <p className="text-sm font-semibold text-foreground">Upload PDF</p>
                          <p className="text-xs text-muted-foreground">Optional</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {/* Info Box */}
            <div className="glass-panel rounded-2xl p-5 border-l-4 border-l-primary">
              <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Why Upload All Three?
              </h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                {[
                  'Detect discrepancies between bureaus (amounts, dates, statuses)',
                  'Find accounts reported to some bureaus but not others',
                  'Identify duplicate accounts across different bureaus',
                  'Maximize your chances of finding FCRA violations'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-2xl p-4 text-center border border-destructive/30 bg-destructive/10 text-destructive">
                {error}
              </div>
            )}

            {/* Progress Bar */}
            {analyzing && (
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Analyzing your credit reports...</span>
                  <span className="font-medium text-primary">{progress}%</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden bg-border/50">
                  <div
                    className="h-full transition-all duration-300 bg-gradient-to-r from-primary to-accent rounded-full"
                    style={{
                      width: `${progress}%`,
                      boxShadow: '0 0 12px hsl(var(--primary) / 0.5)'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Analyze Button */}
            <button
              onClick={handleAnalyzeClick}
              disabled={!hasAnyFile || analyzing}
              className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-300 ${
                !hasAnyFile || analyzing
                  ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                  : 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground btn-glow hover:opacity-90'
              }`}
            >
              {analyzing ? 'Analyzing...' : hasAnyFile ? 'Analyze Credit Reports' : 'Upload At Least One Report'}
            </button>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-8 text-center text-sm text-muted-foreground animate-reveal" style={{ animationDelay: '200ms' }}>
          <p>Your data is processed securely and never stored permanently</p>
        </div>
      </div>

      {/* Lead Capture Dialog */}
      <LeadCaptureDialog
        open={showLeadDialog}
        onOpenChange={setShowLeadDialog}
        onSubmit={handleLeadSubmit}
      />
    </div>
  );
}
